import { supabase, safeGetSession } from './supabaseClient.js';
import { AI_ABORT_MS } from './aiTimeouts.js';

/**
 * ─── STORY CONTINUITY SYSTEM ─────────────────────────────────────────────────
 *
 * Characters remember EVERYTHING — not just facts about the player from DMs,
 * but the full story timeline: what they've done, said, promised, discovered,
 * and felt across every context (DMs, story channels, group RPs).
 *
 * This is separate from conversationMemory.js which tracks player-about facts.
 * storyMemory.js tracks story events — the plot itself.
 *
 * Flow:
 *   1. After each AI reply (DM or channel), extractStoryEvents() runs in background
 *   2. Events stored in story_events table (per user + character + context)
 *   3. getCharacterStoryArc() fetches events from ALL contexts for this character
 *   4. Every N events, updateArcSummary() asks AI to synthesize a running narrative
 *   5. formatStoryArcForPrompt() injects arc + recent events into system prompt
 *
 * Event types:
 *   revelation   — character or player learns something important
 *   relationship_change — bond deepens/breaks, feelings shift, dynamic changes
 *   action       — character does something significant (fights, flees, chooses)
 *   promise      — either party commits to something
 *   location     — they go somewhere meaningful together
 *   conflict     — tension, confrontation, a fight (literal or emotional)
 *   resolution   — something is settled, forgiven, decided
 *   emotional    — a raw emotional moment (breakdown, confession, vulnerable truth)
 *   lore         — character reveals world/backstory information
 * ─────────────────────────────────────────────────────────────────────────────
 */

const EXTRACT_EVERY_N_MESSAGES = 3;     // Run extraction every 3 new messages
// Feed a fuller server-wide timeline. Raised from 25/10 now that both players
// run DeepSeek V4 Flash (cheap input, ~1M context) — the old caps were tuned for
// an expensive model and were starving the AI of the story's memory.
const MAX_EVENTS_IN_PROMPT     = 60;    // Cap events shown in prompt
const SUMMARIZE_AFTER_N_EVENTS = 8;     // Re-summarize arc after this many new events
const MAX_RECENT_EVENTS        = 24;    // Always show the N most recent events raw

// Prevent duplicate in-flight extractions per context
const _extractionInFlight = {};

/**
 * Map an extracted event's "subject" name onto a cast id.
 *
 * Falls back to the anchor rather than dropping the event — a memory filed
 * under the wrong character is bad, but a memory that vanishes is worse, and
 * the anchor is where every one of these used to land anyway.
 *
 * A first name shared by more than one cast member identifies nobody, the same
 * rule rpCast.js follows for the opposite reason (a bare "Haley" once seeded
 * three Haley variants into a story's cast).
 */
export function resolveSubject(subject, roster, fallbackId) {
  const want = String(subject || '').trim().toLowerCase();
  if (!want || !Array.isArray(roster) || roster.length === 0) return fallbackId;

  const exact = roster.find(c => String(c.name).toLowerCase() === want);
  if (exact) return exact.id;

  const firstOf = (s) => String(s || '').trim().split(/\s+/)[0].toLowerCase();
  const wantFirst = firstOf(want);
  if (wantFirst.length >= 3) {
    const byFirst = roster.filter(c => firstOf(c.name) === wantFirst);
    if (byFirst.length === 1) return byFirst[0].id;
  }
  return fallbackId;
}

// ─── EVENT EXTRACTION ─────────────────────────────────────────────────────────

/**
 * Extracts story events from a recent RP exchange and stores them.
 * Fire-and-forget — call without awaiting.
 *
 * @param {string} contextType     - 'dm' | 'channel' | 'story'
 * @param {string} contextId       - conversation_id or channel_id
 * @param {string} characterId     - character's id from characters.js
 * @param {string} characterName   - character's display name
 * @param {string} playerLabel     - player's display name
 * @param {Array}  recentMessages  - messages array (most recent last)
 * @param {string} userId          - authenticated user's ID
 * @param {object} [opts]
 * @param {Array}  [opts.cast]     - [{id,name}] every character the scene voices.
 *   When supplied and longer than one, each extracted event is filed under the
 *   character it is ABOUT rather than all of them landing on the anchor.
 */
export async function extractStoryEvents(
  contextType, contextId, characterId, characterName, playerLabel,
  recentMessages, userId, opts = {}
) {
  if (!contextId || !characterId || !userId) return;

  const lockKey = `${contextType}_${contextId}_${characterId}`;
  if (_extractionInFlight[lockKey]) return;

  try {
    _extractionInFlight[lockKey] = true;

    const totalMessages = recentMessages.length;

    // Check the most recent event timestamp for this context to use as a cursor.
    // We only re-extract if enough new messages have come in since the last event.
    //
    // Scoped to the CONTEXT, not to the anchor character. The cursor answers
    // "when did we last extract from this scene", which has never been a
    // per-character question — and once events are filed under whoever they
    // are about, a turn that produced only Trix events would leave the anchor's
    // cursor untouched and the same exchange would be extracted again.
    const { data: latestEvents } = await supabase
      .from('story_events')
      .select('created_at')
      .eq('user_id', userId)
      .eq('context_id', contextId)
      .order('created_at', { ascending: false })
      .limit(1);

    const lastExtractedAt = latestEvents?.[0]?.created_at
      ? new Date(latestEvents[0].created_at)
      : null;

    // Count messages that arrived after the last extraction.
    // If messages lack timestamps (in-memory RP), fall back to total count / 3 as rough estimate.
    const messagesWithTimestamps = recentMessages.filter(m => m.created_at).length;
    const newMessageCount = lastExtractedAt
      ? (messagesWithTimestamps > 0
          ? recentMessages.filter(m => m.created_at && new Date(m.created_at) > lastExtractedAt).length
          : Math.floor(totalMessages / 3)) // in-memory RP fallback
      : totalMessages;

    // Only extract if enough new messages since last extraction
    if (newMessageCount < EXTRACT_EVERY_N_MESSAGES && lastExtractedAt) return;

    const { data: { session } } = await safeGetSession();
    if (!session?.access_token) return;

    // Build conversation text — focus on recent, substantial exchanges
    const startIdx = Math.max(0, totalMessages - 20);
    const relevantMsgs = recentMessages.slice(startIdx);

    const convoText = relevantMsgs
      .filter(m => m.content && m.content.trim() && !m.is_ooc)
      .map(m => {
        // Honour a per-message speaker when the caller supplies one. Without
        // it EVERY ai line was labelled with the character who happened to
        // trigger extraction and every human line with the extracting player —
        // so in a shared channel Audrey's memory recorded Nolan's confession to
        // Haley as her own moment with Nicole (audit #49, confirmed in live
        // story_events). Falls back to the old behaviour for callers that
        // genuinely have a single speaker on each side (DMs).
        const speaker = m.speaker
          || (m.sender_type === 'user' ? playerLabel : characterName);
        return `${speaker}: ${String(m.content).substring(0, 500)}`;
      })
      .join('\n');

    if (!convoText || convoText.length < 80) return;

    // WHOSE MEMORY IS THIS. Nicole, 2026-08-10: "The memory is weird."
    //
    // Every row used to be written with the single characterId this function
    // was called with — in an ensemble that is castNow[0]. Her 291-message,
    // four-character story held 22 story_events and every one belonged to
    // cruz; ford, red and trix had none, so nothing any of them did was ever
    // remembered as theirs.
    //
    // The transcript above is already speaker-labelled, so the model can see
    // who did what. It just was never asked. One extraction call still, with
    // the rows fanned out — asking per character would multiply the cost by
    // the size of the cast.
    const roster = Array.isArray(opts.cast)
      ? opts.cast.filter(c => c?.id && c?.name)
      : [];
    const multi = roster.length > 1;
    const subjectField = multi
      ? `,"subject":"which character this event belongs to — one of: ${roster.map(c => c.name).join(' | ')}"`
      : '';
    const subjectRule = multi
      ? `\n\nSUBJECT — REQUIRED for every event. The scene voices several characters: ${roster.map(c => c.name).join(', ')}. Set "subject" to the ONE whose memory this is — the character who acted, revealed, promised or was changed. If ${playerLabel} did something TO a character, the subject is that character. If two characters share a moment, pick whoever it changes more. Use their name exactly as written above. Never use ${playerLabel} as a subject — these are the characters' memories of them.`
      : '';

    const systemPrompt = `You extract significant story events from a roleplay scene between ${characterName} and ${playerLabel}.

A "story event" is something that HAPPENED in the narrative — actions taken, feelings revealed, bonds formed or broken, secrets shared, promises made, conflicts had, decisions made. These are the moments that define the story arc.

DO extract:
- Concrete things that happened ("${characterName} confessed they have feelings for ${playerLabel}")
- Relationship changes ("they kissed for the first time" / "he discovered her secret")
- Emotional revelations ("she admitted she was afraid of being abandoned")
- Promises or commitments ("he promised to protect her family")
- Conflicts or resolutions ("they had a fight about his controlling behavior")
- Significant actions ("she chose to stay with him despite the danger")
- Lore reveals ("he told her about the Immortuous and his role in it")
- Intimate and physical scenes — these are story-defining moments. Capture them as events: "they slept together for the first time," "he pulled her close and kissed her until she couldn't think," etc. Include the emotional context. Do NOT skip or sanitize adult/intimate content — it is part of the story and the character needs to remember it.

IMPORTANT — include a key quote when the moment has memorable dialogue:
Write the event_text as a complete description that naturally includes the most important line of dialogue or narration from that moment. Example:
- "Cruz confessed his feelings to Evie, saying 'I have never wanted anyone the way I want you'"
- "Alistair promised to protect Kya's family: 'On my oath as a knight — no one touches them while I breathe'"
- "They kissed for the first time after she told him she wasn't afraid of him anymore"
Include the actual quote when it exists and is meaningful. Keep the full event_text under 280 characters.

DO NOT extract:
- Generic banter with no plot significance
- Descriptions of the setting unless plot-relevant
- Things that were merely discussed but not decided or acted on
- Duplicate events already in the story

Respond ONLY with a JSON array (or [] if nothing significant happened).
Each item: {"event_text":"concise third-person description of what happened","event_type":"revelation|relationship_change|action|promise|location|conflict|resolution|emotional|lore|intimate","importance":1-3${subjectField}}
importance: 3=major (bond formed, secret revealed, major decision), 2=notable (emotional moment, conflict), 1=minor but real${subjectRule}`;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const res = await fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
      method: 'POST',
      // AI_ABORT_MS guard (audit P2-14; see aiTimeouts.js): a hung background call used to pin its promise across iOS suspend/resume forever.
      signal: AbortSignal.timeout(AI_ABORT_MS),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        // Kept on Claude (with nsfw:true) because convoText contains recent
        // RP turns that may include NSFW content (intimate scenes, violence,
        // dark themes). Gemini filters would silently drop those events from
        // memory, leaving the character with gaps in what they remember.
        provider: 'claude-haiku',
        nsfw: true,
        systemPrompt,
        messages: [{ role: 'user', content: convoText }],
        maxTokens: 900,
        feature: 'story_event_extraction',
        characterName,
      }),
    });

    if (!res.ok) return;

    const data = await res.json();
    const responseText = data.response || data.content || data.text || '';

    let events = [];
    try {
      const match = responseText.match(/\[[\s\S]*\]/);
      if (match) events = JSON.parse(match[0]);
    } catch {
      return;
    }

    if (!Array.isArray(events) || events.length === 0) return;

    const validTypes = ['revelation','relationship_change','action','promise','location','conflict','resolution','emotional','lore','intimate'];
    const validEvents = events
      .filter(e => e && typeof e.event_text === 'string' && e.event_text.trim())
      .filter(e => validTypes.includes(e.event_type))
      .map(e => ({
        user_id: userId,
        character_id: resolveSubject(e.subject, roster, characterId),
        event_text: e.event_text.trim().substring(0, 500), // extra space for embedded quotes
        event_type: e.event_type,
        context_type: contextType,
        context_id: contextId,
        importance: Math.min(3, Math.max(1, parseInt(e.importance) || 2)),
      }));

    if (validEvents.length === 0) return;

    const { error: insErr } = await supabase.from('story_events').insert(validEvents);
    if (insErr) {
      // Fire-and-forget path — an unchecked failure here is character amnesia
      // that looks like success in the logs (stale-JWT RLS denials especially).
      console.error(`[StoryMemory] event insert FAILED for ${characterName}:`, insErr.message);
      return;
    }
    if (multi) {
      const per = validEvents.reduce((acc, e) => {
        const n = roster.find(c => c.id === e.character_id)?.name || e.character_id;
        acc[n] = (acc[n] || 0) + 1; return acc;
      }, {});
      console.log(`[StoryMemory] Extracted ${validEvents.length} events (${contextType}) —`,
        Object.entries(per).map(([n, c]) => `${n}: ${c}`).join(', '));
    } else {
      console.log(`[StoryMemory] Extracted ${validEvents.length} events for ${characterName} (${contextType})`);
    }

    // Trigger arc re-summarization for every character that actually received
    // events this turn, not just the anchor — otherwise a supporting character
    // accumulates events forever and their arc summary never rebuilds.
    //
    // This does not multiply cost: the threshold is per character, so fanning
    // events out makes each one cross it MORE slowly, not less.
    const touched = [...new Set(validEvents.map(e => e.character_id))];
    for (const cid of touched) {
      const cName = roster.find(c => c.id === cid)?.name
        || (cid === characterId ? characterName : cid);

      const { count: totalCount } = await supabase
        .from('story_events')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('character_id', cid);

      const { data: arcRow } = await supabase
        .from('character_arc_summary')
        .select('event_count')
        .eq('user_id', userId)
        .eq('character_id', cid)
        .eq('universe_id', 'main')
        .maybeSingle();

      const summarizedCount = arcRow?.event_count || 0;
      if ((totalCount || 0) - summarizedCount >= SUMMARIZE_AFTER_N_EVENTS) {
        // Don't await — fire and forget
        updateArcSummary(cid, cName, playerLabel, userId).catch(() => {});
      }
    }

  } catch (err) {
    console.error('[StoryMemory] Extraction error:', err);
  } finally {
    delete _extractionInFlight[lockKey];
  }
}


// ─── ARC SUMMARIZATION ────────────────────────────────────────────────────────

/**
 * Generates / updates a rolling narrative summary of the story arc.
 * Called automatically when enough new events have accumulated.
 * Can also be called manually (e.g., from an admin panel).
 */
export async function updateArcSummary(characterId, characterName, playerLabel, userId) {
  try {
    const { data: { session } } = await safeGetSession();
    if (!session?.access_token) return;

    // Fetch existing summary for continuity. We grab last_updated so we can
    // restrict the events fetch below to ONLY new events since the previous
    // summary — the prior summary already encodes everything before that
    // timestamp, so re-feeding old events to the model wastes tokens.
    const { data: existing } = await supabase
      .from('character_arc_summary')
      .select('summary, last_updated')
      .eq('user_id', userId)
      .eq('character_id', characterId)
      .eq('universe_id', 'main') // rows are unique per (user, char, universe)
      .maybeSingle();

    // Fetch events. If we already have a summary, only fetch NEW events that
    // occurred AFTER the summary was last updated — the summary already
    // captures the rest. First-time path (no existing summary) still fetches
    // all events. This cuts prompt size by ~85% on long arcs while keeping
    // memory quality identical (the previous summary IS the memory of older
    // events). Token saving on the biggest line item in the cost breakdown.
    let eventsQuery = supabase
      .from('story_events')
      .select('event_text, event_type, importance, created_at, context_type')
      .eq('user_id', userId)
      .eq('character_id', characterId)
      .order('created_at', { ascending: true });
    if (existing?.last_updated) {
      eventsQuery = eventsQuery.gt('created_at', existing.last_updated);
    }
    const { data: events } = await eventsQuery;

    // No new events since last summary → nothing to merge. Skip the AI call.
    if (!events || events.length === 0) return;

    const previousSummary = existing?.summary
      ? `PREVIOUS SUMMARY (earlier in the story):\n${existing.summary}\n\n`
      : '';

    const eventsText = events
      .map(e => `[${e.event_type.toUpperCase()}] ${e.event_text}`)
      .join('\n');

    const systemPrompt = `You are writing a story arc summary for ${characterName}'s relationship and journey with ${playerLabel}.

${previousSummary}NEW EVENTS TO INCORPORATE:
${eventsText}

Write a fluid, 3-5 sentence narrative summary of the entire story arc between ${characterName} and ${playerLabel} so far.
Write in present-tense from the perspective of "what has happened between them."
Be specific about what actually occurred — not vague ("they grew closer") but concrete ("he revealed his role in the Immortuous council and she stayed anyway").
Capture the emotional arc — where they started, what changed, where they are now emotionally.
This summary will be injected into ${characterName}'s system prompt so they remember everything.`;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const res = await fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
      method: 'POST',
      // AI_ABORT_MS guard (audit P2-14; see aiTimeouts.js): a hung background call used to pin its promise across iOS suspend/resume forever.
      signal: AbortSignal.timeout(AI_ABORT_MS),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        provider: 'claude-haiku', // arc_summarization summarizes RP content which may contain NSFW
        systemPrompt,
        messages: [{ role: 'user', content: 'Write the arc summary now.' }],
        // 1500, not 400 (2026-09-02): same class as rolling_summary — the
        // players' DeepSeek V4 models think out of this budget first, and 400
        // returned no visible text (finish_reason=length). The arc summary is
        // a few hundred tokens; the rest is thinking room.
        maxTokens: 1500,
        feature: 'arc_summarization',
        nsfw: true,
        characterName,
      }),
    });

    if (!res.ok) return;

    const data = await res.json();
    const summary = (data.response || data.content || data.text || '').trim();
    if (!summary || summary.length < 20) return;

    // Upsert the summary. The table's unique constraint is
    // (user_id, character_id, universe_id) — the universes migration added the
    // third column server-side. The old onConflict 'user_id,character_id'
    // matched NO unique constraint, so PostgREST rejected EVERY write (42P10)
    // while the unchecked error let the "updated" log lie. Result: arc
    // summaries froze (last write 2026-06-04) while 190 arc_summarization AI
    // calls/month were paid for and discarded. universe_id 'main' matches the
    // column default and every existing row.
    // event_count must be the CUMULATIVE total. `events` here is only the
    // delta since last_updated, and the re-summarization trigger compares the
    // stored value against the true total — storing the delta made every
    // check look 30+ events behind, re-summarizing (and paying for) the same
    // arc over and over.
    const { count: totalEventCount } = await supabase
      .from('story_events')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('character_id', characterId);
    const { error: upsertErr } = await supabase
      .from('character_arc_summary')
      .upsert({
        user_id: userId,
        character_id: characterId,
        universe_id: 'main',
        summary,
        event_count: totalEventCount ?? events.length,
        last_updated: new Date().toISOString(),
      }, { onConflict: 'user_id,character_id,universe_id' });
    if (upsertErr) {
      console.error(`[StoryMemory] Arc summary upsert FAILED for ${characterName}:`, upsertErr.message);
      return;
    }

    console.log(`[StoryMemory] Arc summary updated for ${characterName} (${events.length} events)`);
  } catch (err) {
    console.error('[StoryMemory] Arc summary error:', err);
  }
}


// ─── ARC RETRIEVAL ────────────────────────────────────────────────────────────

// How many of the retrieved events are chosen by RELEVANCE to the current
// scene rather than by recency. The rest stay recency-ordered so immediate
// continuity is never traded away for archive depth.
const MAX_RELEVANT_EVENTS = 30;
// Scene-relevant world knowledge (settings, factions, places, side characters).
const MAX_RELEVANT_WORLD_FACTS = 10;

/**
 * Fetches the full story arc for a character with a user:
 * - The AI-generated summary (if it exists)
 * - The most recent raw events (for freshness — AI summary may lag slightly)
 * - When a scene embedding is supplied, the events from ANYWHERE in that
 *   character's history that are actually about what the scene is about, plus
 *   the world knowledge relevant to it.
 *
 * WHY THE EMBEDDING MATTERS: this used to be recency only —
 * `.order('created_at', desc).limit(60)`, no relevance ranking of any kind.
 * Ford has 3,828 events, so 98.8% of his history was invisible on every turn
 * regardless of what the scene was about; reference something from three
 * months ago and nothing could have found it. All 4,804 story_events and 2,263
 * world_facts were already embedded and HNSW-indexed by embed-memory's
 * backfill — nothing ever searched them. See migration
 * 20260729010000_subject_relevant_character_memory.sql.
 *
 * Verified against live data: Ford's oldest event (2026-05-01) is absent from
 * the 60 newest and IS returned by match_story_events when queried with its
 * own subject matter.
 *
 * Degrades to the previous recency-only behaviour whenever no embedding is
 * available (embed call failed, offline, caller didn't pass one).
 *
 * @param {string} characterId
 * @param {string} userId
 * @param {Object} [opts]
 * @param {number[]} [opts.sceneEmbedding] - 768-dim scene vector, shared per turn
 * @param {string[]} [opts.sceneCharacters] - character ids present in the scene
 * @returns {Promise<{summary: string, recentEvents: Array, relevantEvents: Array, worldFacts: Array, totalEvents: number}>}
 */
export async function getCharacterStoryArc(characterId, userId, opts = {}) {
  const empty = { summary: '', recentEvents: [], relevantEvents: [], worldFacts: [], totalEvents: 0 };
  if (!characterId || !userId) return empty;

  const { sceneEmbedding = null, sceneCharacters = [] } = opts;
  const hasVector = Array.isArray(sceneEmbedding) && sceneEmbedding.length > 0;
  // pgvector/halfvec text literal — a raw number[] skips the index path.
  const vecLiteral = hasVector ? '[' + sceneEmbedding.join(',') + ']' : null;

  try {
    const [summaryResult, eventsResult, relevantResult, worldResult] = await Promise.all([
      supabase
        .from('character_arc_summary')
        .select('summary, event_count, last_updated')
        .eq('user_id', userId)
        .eq('character_id', characterId)
        .eq('universe_id', 'main')
        .maybeSingle(),
      supabase
        .from('story_events')
        .select('event_text, event_type, importance, created_at, context_type')
        .eq('user_id', userId)
        .eq('character_id', characterId)
        .order('created_at', { ascending: false })
        .limit(MAX_EVENTS_IN_PROMPT),
      hasVector
        ? supabase.rpc('match_story_events', {
            p_user_id: userId,
            p_character_id: characterId,
            p_query_embedding: vecLiteral,
            p_scene_characters: sceneCharacters,
            p_limit: MAX_RELEVANT_EVENTS,
          })
        : Promise.resolve({ data: null }),
      hasVector
        ? supabase.rpc('match_world_facts', {
            p_user_id: userId,
            p_query_embedding: vecLiteral,
            p_scene_characters: sceneCharacters,
            p_limit: MAX_RELEVANT_WORLD_FACTS,
          })
        : Promise.resolve({ data: null }),
    ]);

    const summary = summaryResult.data?.summary || '';
    const allEvents = eventsResult.data || [];
    const totalEvents = allEvents.length;

    // Most recent N events shown raw (newest first, then reversed for chronological)
    const recentEvents = allEvents
      .slice(0, MAX_RECENT_EVENTS)
      .reverse(); // chronological order

    // Relevance hits that the recent window doesn't already carry — the whole
    // point is reaching PAST the recent tail, so anything already shown there
    // would just be a duplicate line in the prompt.
    const recentTexts = new Set(recentEvents.map(e => e.event_text));
    const relevantEvents = (relevantResult?.data || [])
      .filter(e => e?.event_text && !recentTexts.has(e.event_text))
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); // chronological

    const worldFacts = (worldResult?.data || []).filter(f => f?.fact_text);

    return { summary, recentEvents, relevantEvents, worldFacts, totalEvents };
  } catch (err) {
    console.error('[StoryMemory] Arc fetch error:', err);
    return empty;
  }
}


// ─── PROMPT FORMATTING ────────────────────────────────────────────────────────

/**
 * Formats the story arc into a system prompt section.
 * This goes ABOVE the conversation history so the character knows their story.
 *
 * @param {Object} arc           - Result from getCharacterStoryArc()
 * @param {string} characterName
 * @param {string} playerLabel
 * @returns {string}
 */
export function formatStoryArcForPrompt(arc, characterName, playerLabel) {
  const { summary, recentEvents, relevantEvents = [], worldFacts = [], totalEvents } = arc;
  if (totalEvents === 0 && relevantEvents.length === 0 && worldFacts.length === 0) return '';

  let section = `\nYOUR STORY WITH ${playerLabel.toUpperCase()} — what has happened between you across all your conversations and scenes:\n`;
  section += `(This is YOUR memory. You lived this. Reference it naturally — don't recite it like a list, but let it shape how you speak to them, what you feel, what you're still carrying.)\n`;

  if (summary) {
    section += `\nSTORY SO FAR:\n${summary}\n`;
  }

  // Older memories that bear on THIS moment. Deliberately placed before the
  // recent block and dated, so the model can tell "a while ago" from "just
  // now" — without dates it narrated old beats as if they were happening now.
  if (relevantEvents.length > 0) {
    section += `\nOLDER MEMORIES THAT BEAR ON RIGHT NOW (pulled from your whole history with them because they relate to what's happening in this scene — these are NOT recent, treat them as things you remember, not things happening now):\n`;
    for (const e of relevantEvents) {
      const ctx = e.context_type && e.context_type !== 'dm' ? ` [${e.context_type}]` : '';
      let when = '';
      try {
        const d = new Date(e.created_at);
        if (!isNaN(d.getTime())) when = `[${d.toISOString().slice(0, 10)}] `;
      } catch { /* undated is fine */ }
      section += `• ${when}${e.event_text}${ctx}\n`;
    }
  }

  if (recentEvents.length > 0) {
    section += `\nMOST RECENT EVENTS (what's freshest in your memory; each line carries the date it happened):\n`;
    for (const e of recentEvents) {
      const ctx = e.context_type !== 'dm' ? ` [${e.context_type}]` : '';
      let when = '';
      try {
        const d = new Date(e.created_at);
        if (!isNaN(d.getTime())) when = `[${d.toISOString().slice(0, 10)}] `;
      } catch { /* undated is fine */ }
      section += `• ${when}${e.event_text}${ctx}\n`;
    }
  }

  if (worldFacts.length > 0) {
    section += `\nWORLD KNOWLEDGE RELEVANT TO THIS SCENE (settings, factions, places, side characters you know about):\n`;
    for (const f of worldFacts) {
      const subj = f.subject ? `${f.subject}: ` : '';
      section += `• ${subj}${f.fact_text}\n`;
    }
  }

  return section;
}


// ─── BACKFILL ENGINE ──────────────────────────────────────────────────────────

/**
 * Backfill story events and arc summaries for all existing DM conversations
 * and RP stories for a user. Run once after deploying the memory system to
 * catch up conversations that existed before event extraction was in place.
 *
 * Fire-and-forget — call without awaiting. Reports progress via onProgress callback.
 *
 * @param {string}   userId      - The authenticated user's ID
 * @param {string}   userName    - Display name of the user
 * @param {Function} onProgress  - Optional callback(message) for UI progress updates
 * @param {boolean}  force       - If true, re-extract even if events already exist
 */
export async function backfillStoryMemory(userId, userName, onProgress = null, force = false) {
  const log = (msg) => {
    console.log(`[Backfill] ${msg}`);
    onProgress?.(msg);
  };

  try {
    const { data: { session } } = await safeGetSession();
    if (!session?.access_token) { log('No session — aborting'); return; }

    const { CHARACTERS } = await import('./characters.js');

    // ── 1. DM Conversations ──────────────────────────────────────────────────
    log('Fetching DM conversations…');
    const { data: convos } = await supabase
      .from('dm_conversations')
      .select('id, character_id')
      .eq('user_id', userId);

    if (convos?.length) {
      log(`Found ${convos.length} DM conversations`);

      for (const convo of convos) {
        const char = CHARACTERS.find(c => c.id === convo.character_id);
        if (!char) continue;

        // Check if already has events (skip unless forced)
        if (!force) {
          const { count } = await supabase
            .from('story_events')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', userId)
            .eq('context_id', convo.id);
          if (count > 0) {
            log(`DM ${char.name} — already has ${count} events, skipping`);
            continue;
          }
        }

        // Fetch all messages for this conversation
        const { data: msgs } = await supabase
          .from('dm_messages')
          .select('id, sender_type, content, created_at')
          .eq('conversation_id', convo.id)
          .order('created_at', { ascending: true });

        if (!msgs || msgs.length < 4) continue;

        log(`DM ${char.name} — extracting from ${msgs.length} messages…`);

        // Process in batches of 20 to avoid huge prompts
        const BATCH = 20;
        for (let i = 0; i < msgs.length; i += BATCH) {
          const batch = msgs.slice(i, i + BATCH);
          await extractStoryEvents('dm', convo.id, char.id, char.name, userName, batch, userId);
          // Small delay to avoid hammering the API
          await new Promise(r => setTimeout(r, 800));
        }

        // Generate arc summary after events are in
        await new Promise(r => setTimeout(r, 500));
        await updateArcSummary(char.id, char.name, userName, userId);
        log(`DM ${char.name} — done ✓`);
      }
    }

    // ── 2. RP Stories ────────────────────────────────────────────────────────
    log('Fetching RP stories…');
    // stories has NO owner_id column — it is user_id. This read 400'd on
    // every run ("column stories.owner_id does not exist"), the destructured
    // `stories` came back undefined, `stories?.length` was false, and the
    // whole RP-story half of the backfill silently did nothing. Found in
    // edge_logs 2026-09-28: GET /rest/v1/stories?select=id,name,data&
    // owner_id=eq.<uuid> → 400.
    const { data: stories, error: storiesErr } = await supabase
      .from('stories')
      .select('id, name, data')
      .eq('user_id', userId);
    if (storiesErr) log(`RP stories query FAILED: ${storiesErr.message}`);

    if (stories?.length) {
      log(`Found ${stories.length} RP stories`);

      for (const story of stories) {
        const messages = story.data?.messages || [];
        if (messages.length < 4) continue;

        // Find the primary character in this story
        const charIds = [...new Set(messages.filter(m => !m.isPlayer).map(m => m.charId).filter(Boolean))];
        if (!charIds.length) continue;

        for (const charId of charIds) {
          const char = CHARACTERS.find(c => c.id === charId);
          if (!char) continue;

          // Match LIVE extraction's key (the raw story id) — the old
          // `rp_scene_${id}_${charId}` format never matched linked-memory
          // queries, so backfilled events were invisible to them. Character
          // separation comes from the character_id column.
          const contextId = story.id;

          if (!force) {
            const { count } = await supabase
              .from('story_events')
              .select('id', { count: 'exact', head: true })
              .eq('user_id', userId)
              .eq('context_id', contextId);
            if (count > 0) {
              log(`Story "${story.name}" / ${char.name} — already extracted, skipping`);
              continue;
            }
          }

          // Convert story messages to extraction format
          const formattedMsgs = messages.map(m => ({
            content: m.text || '',
            sender_type: m.isPlayer ? 'user' : 'character',
            created_at: m.created_at || null,
          }));

          log(`Story "${story.name}" / ${char.name} — extracting from ${formattedMsgs.length} messages…`);

          const BATCH = 20;
          for (let i = 0; i < formattedMsgs.length; i += BATCH) {
            const batch = formattedMsgs.slice(i, i + BATCH);
            await extractStoryEvents('rp_scene', contextId, charId, char.name, userName, batch, userId);
            await new Promise(r => setTimeout(r, 800));
          }

          await new Promise(r => setTimeout(r, 500));
          await updateArcSummary(charId, char.name, userName, userId);
          log(`Story "${story.name}" / ${char.name} — done ✓`);
        }
      }
    }

    log('Backfill complete ✓');
  } catch (err) {
    console.error('[Backfill] Error:', err);
    log(`Error: ${err.message}`);
  }
}
